// Step 12 — DownloadButton.
//
// Two variants:
//   • lineart — fetches the static lineart PNG and produces an A4 PDF
//   • png     — fetches the user's R2 artwork and saves as PNG
//
// Two layout modes:
//   • 'icon'  — round icon-only (Step 11 ShareButton.icon style, deprecated)
//   • 'pill'  — icon + Chinese label for the ColoringPage toolbar (default)
//
// Filename is always computed via `exportFilename(packId, itemId, ext, { isBonus })`
// so bonus snake_case IDs (joseph_panorama) hyphenize consistently.
//
// PDF embeds a metadata footer (nameZh / nameEn / scriptureRef) — passed
// via `pdfMeta`. PNG receives an optional watermark via `pngWatermark`.

import { useState } from 'react'
import { Download, FileText, Image, Loader2 } from 'lucide-react'
import type { PackId } from '@skena/schema'
import {
  ExportError,
  downloadArtworkPngWithRetry,
  downloadLineartPdf,
  exportFilename,
  type PngWatermark,
} from '../lib/exports.ts'

interface PdfMetadata {
  nameZh?: string
  nameEn?: string
  scriptureRef?: string
}

interface DownloadButtonProps {
  variant: 'lineart' | 'png'
  layout?: 'icon' | 'pill'
  packId: PackId
  itemId: string
  isBonus?: boolean
  artworkUrl?: string
  onArtworkUrlRefresh?: () => Promise<string>
  /** Metadata embedded in the PDF footer band (lineart variant only). */
  pdfMeta?: PdfMetadata
  /** Watermark line on the PNG (png variant only). */
  pngWatermark?: PngWatermark
  ariaLabel: string
  /** Visible label text in pill mode (e.g. "下載著色圖"). */
  label?: string
  className?: string
  disabled?: boolean
}

export function DownloadButton({
  variant,
  layout = 'pill',
  packId,
  itemId,
  isBonus = false,
  artworkUrl,
  onArtworkUrlRefresh,
  pdfMeta,
  pngWatermark,
  ariaLabel,
  label,
  className = '',
  disabled = false,
}: DownloadButtonProps) {
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  // Defensive: PNG variant needs an artworkUrl. If absent, don't render.
  if (variant === 'png' && !artworkUrl) return null

  const onTap = async () => {
    if (busy || disabled) return
    setBusy(true)
    setToast('準備中…')
    try {
      if (variant === 'lineart') {
        await downloadLineartPdf(packId, itemId, { isBonus, ...pdfMeta })
      } else {
        const filename = exportFilename(packId, itemId, 'png', { isBonus })
        const refresh = onArtworkUrlRefresh ?? (async () => artworkUrl!)
        await downloadArtworkPngWithRetry(refresh, artworkUrl!, filename, pngWatermark)
      }
      setToast('已下載')
      window.setTimeout(() => setToast(null), 2400)
    } catch (err) {
      const reason = err instanceof ExportError ? err.reason : 'unknown'
      console.error('[download] failed', { variant, packId, itemId, reason, err })
      setToast('再試一次')
      window.setTimeout(() => setToast(null), 2400)
    } finally {
      setBusy(false)
    }
  }

  const Icon = variant === 'lineart' ? FileText : Image

  if (layout === 'icon') {
    return (
      <>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void onTap()
          }}
          disabled={busy || disabled}
          aria-label={ariaLabel}
          aria-busy={busy}
          className={`rounded-full bg-white/90 p-2 text-[var(--color-fg)] shadow-sm transition-shadow hover:shadow-md disabled:cursor-wait disabled:opacity-70 ${className}`}
        >
          {busy ? (
            <Loader2 size={18} aria-hidden className="animate-spin" />
          ) : (
            <Icon size={18} aria-hidden />
          )}
        </button>
        <DownloadToast toast={toast} />
      </>
    )
  }

  // Pill layout — text + icon for the ColoringPage toolbar.
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          void onTap()
        }}
        disabled={busy || disabled}
        aria-label={ariaLabel}
        aria-busy={busy}
        className={`inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 bg-white px-3 py-1.5 text-xs font-medium text-[var(--color-fg)] shadow-sm transition-shadow hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      >
        {busy ? (
          <Loader2 size={14} aria-hidden className="animate-spin" />
        ) : (
          <>
            <Download size={14} aria-hidden />
            <Icon size={14} aria-hidden />
          </>
        )}
        {label ? <span>{label}</span> : null}
      </button>
      <DownloadToast toast={toast} />
    </>
  )
}

function DownloadToast({ toast }: { toast: string | null }) {
  if (!toast) return null
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed top-6 left-1/2 z-50 -translate-x-1/2 rounded-[var(--radius-pill)] bg-[var(--color-fg)] px-4 py-2 text-sm text-white shadow"
    >
      {toast}
    </div>
  )
}
